import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import { type ZIndex } from 'shared/constants/zIndex'
import { IntegrationKey, IntegrationsApi, type IResponseMergeField } from 'entities/Integrations'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type Integration } from 'entities/Integrations/model/Integration'
import { MergeField } from 'entities/MergeField/model/MergeField'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export type IMergeFieldConfig = {
  contact?: Contact | null
  integration?: Integration | null
  onAddMergeField?: ((field: MergeField) => void | boolean) | null
  isAllIntegration?: boolean
  isAllConnectedOrDisconnectedIntegration?: boolean
  isSelectDefault?: boolean
  useAllMergeFields?: boolean
  showOnlyWithValue?: boolean
  includeIntegrations?: IntegrationKey[]
  excludeFieldsIntegrations?: { [key in IntegrationKey]?: string[] }
  onClose?: () => void
  zIndex?: ZIndex
  sizeTriggerButton?: 'small' | 'medium'
  isNoAutoFocus?: boolean
}

export class MergeFieldsStore {
  private _contact: Contact | null
  private _integration: Integration | null
  public onAddMergeField: ((field: MergeField) => void | boolean) | null
  private _search = ''
  private _loading = false
  private _selectedIntegration: Integration | null = null
  private _isSelectDefault = false
  private _isAllIntegration = false
  private _isAllConnectedOrDisconnectedIntegration = false
  private _useAllMergeFields = false
  private _showOnlyWithValue = false
  private _mergeFields: MergeField[] = []
  private _includeIntegrations: IntegrationKey[] = []
  private _excludeFieldsIntegrations: { [key in IntegrationKey]?: string[] } = {}

  constructor(
    {
      contact,
      integration,
      onAddMergeField,
      isAllIntegration,
      isAllConnectedOrDisconnectedIntegration,
      isSelectDefault,
      useAllMergeFields,
      showOnlyWithValue,
      includeIntegrations,
      excludeFieldsIntegrations,
    }: IMergeFieldConfig,
    private _entities: IEntitiesStore
  ) {
    this._contact = contact || null
    this._integration = integration || null
    this.onAddMergeField = onAddMergeField || null
    this._isAllIntegration = isAllIntegration || isAllConnectedOrDisconnectedIntegration || false
    this._isAllConnectedOrDisconnectedIntegration = isAllConnectedOrDisconnectedIntegration || false
    this._isSelectDefault = isSelectDefault || false
    this._useAllMergeFields = useAllMergeFields || false
    this._showOnlyWithValue = showOnlyWithValue || false
    this._includeIntegrations = includeIntegrations?.length
      ? [...includeIntegrations, IntegrationKey.salesmessage]
      : []
    this._excludeFieldsIntegrations = excludeFieldsIntegrations || {}

    if (!this._integration) {
      void this.loadMergeFields()
    }

    makeAutoObservable(this)
  }

  get integration() {
    return this._integration
  }

  get isAllIntegration() {
    return this._isAllIntegration
  }

  get isSelectDefault() {
    return this._isSelectDefault
  }

  get selectedIntegration() {
    return this._selectedIntegration
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get allIntegrations(): Integration[] {
    const contactIntegration: Integration[] = []

    let allIntegrationsKeys: IntegrationKey[] = [
      IntegrationKey.hubspot,
      IntegrationKey.activecampaign,
      IntegrationKey.infusionsoft,
      IntegrationKey.pipedrive,
      IntegrationKey.salesforce,
    ]

    if (this._includeIntegrations.length) {
      allIntegrationsKeys = this._includeIntegrations
    }

    allIntegrationsKeys.forEach((key) => {
      const integration = this._entities.integrationsStore.getIntegration(key)
      if (integration) {
        if (this._isAllConnectedOrDisconnectedIntegration) {
          const isConnectedOrDisconnected = !!integration.connected || !!integration.disconnected
          if (isConnectedOrDisconnected) {
            contactIntegration.push(integration)
          }
        } else {
          contactIntegration.push(integration)
        }
      }
    })

    return contactIntegration
  }

  get filteredMergeFields() {
    if (this._isAllIntegration) {
      let fields: MergeField[] = []
      if (!this.isSelectedIntegration) {
        if (this._includeIntegrations.length) {
          fields = this._entities.mergeFieldStore.allFields.filter((field) =>
            this._includeIntegrations.includes(field.integrationKey)
          )
        } else {
          fields = this._entities.mergeFieldStore.allFields
        }
      }
      if (this._isSelectDefault) {
        const map = this._entities.mergeFieldStore.mergeFieldsIntegrationsMap.get(
          IntegrationKey.salesmessage
        )
        if (map) {
          fields = Array.from(map.values())
        }
      }
      if (this._selectedIntegration) {
        const map = this._entities.mergeFieldStore.mergeFieldsIntegrationsMap.get(
          this._selectedIntegration.key
        )
        if (map) {
          fields = Array.from(map.values())
        }
      }
      return fields
        .filter((field) => this._isFieldAllowed(field))
        .filter((field) =>
          this._search ? field.label?.toLowerCase().includes(this._search.toLowerCase()) : true
        )
    }
    if (this._isSelectDefault) {
      if (this._useAllMergeFields) {
        let fields: MergeField[] = []
        const map = this._entities.mergeFieldStore.mergeFieldsIntegrationsMap.get(
          IntegrationKey.salesmessage
        )
        if (map) {
          fields = Array.from(map.values())
        }

        return fields
          .filter((field) => this._isFieldAllowed(field))
          .filter((field) =>
            this._search ? field.label?.toLowerCase().includes(this._search.toLowerCase()) : true
          )
      }
      return this._mergeFields
        .filter((field) => this._isFieldAllowed(field))
        .filter(
          (field) => !field.integrationKey || field.integrationKey === IntegrationKey.salesmessage
        )
    }
    if (this._selectedIntegration) {
      return this._mergeFields
        .filter((field) => this._isFieldAllowed(field))
        .filter(
          (field) => field.integrationKey && field.integrationKey !== IntegrationKey.salesmessage
        )
    }

    return this._mergeFields.filter((field) => this._isFieldAllowed(field))
  }

  get isSelectedIntegration() {
    if (this._useAllMergeFields) return null

    return this._isSelectDefault || this._selectedIntegration
  }

  get useAllMergeFields() {
    return this._useAllMergeFields
  }

  private _isFieldAllowed = (field: MergeField) => {
    const excludeFields = this._excludeFieldsIntegrations[field.integrationKey]
    if (excludeFields?.includes(field.name)) return false
    if (this._showOnlyWithValue && !field.value) return false

    return true
  }

  reset = () => {
    this._mergeFields = []
    this._search = ''
  }

  setMergeFields = (mergeFields: IResponseMergeField[]) => {
    this._mergeFields = mergeFields.map((mergeField) => new MergeField(mergeField))
  }

  loadMergeFields = async () => {
    if (!this._contact) {
      return
    }
    try {
      this._loading = true
      const { data } = await IntegrationsApi.getIntegrationsContactMergeFields(
        this._contact.id,
        this._search
      )
      if (data) {
        this.setMergeFields(data)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  setSearch = async (search: string) => {
    this._search = search
    if (!this._isAllIntegration) {
      void this.loadMergeFields()
    }
  }

  onSelectedIntegration = (integration: Integration) => {
    this._selectedIntegration = integration
    void this.loadMergeFields()
  }

  onSelectedDefault = (isSelectDefault: boolean) => {
    this._isSelectDefault = isSelectDefault
    void this.loadMergeFields()
  }

  onBack = () => {
    this._selectedIntegration = null
    this._isSelectDefault = false
    this._search = ''
  }
}
